import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInput } from '../../../forms/components/FormInput/FormInput';
import { InputType } from '../../../forms/types';
import { FieldsName, FieldsType } from './consts';
import { resolver } from './validators';
import { PresaveDateField } from './PresaveDateField';
import { PresaveLinkPrefill } from './props';

interface PresaveLinkFormProps {
  prefill?: PresaveLinkPrefill;
  isLoading: boolean;
  onSuccess: (values: FieldsType) => void;
}

/**
 * The fields of the pre-save block editor (a pre-save block added to a page that exists). A
 * pre-save made from a song uses no typed fields (design presave-create).
 */
export function usePresaveLinkForm({ prefill, isLoading, onSuccess }: PresaveLinkFormProps) {
  const intl = useIntl();
  const today = useMemo(() => date.format(new Date(), 'YYYY-MM-DD'), []);
  const defaultValues = useMemo<FieldsType>(
    () => ({
      [FieldsName.title]: prefill?.title ?? '',
      [FieldsName.artistName]: prefill?.artistName ?? '',
      [FieldsName.releaseDate]: prefill?.releaseDate ?? '',
    }),
    [prefill]
  );
  const { control, handleSubmit } = useForm<FieldsType>({ resolver, defaultValues });

  const onSubmit: SubmitHandler<FieldsType> = useCallback(
    (values) => {
      onSuccess(values);
    },
    [onSuccess]
  );

  const submit = useCallback(
    (event?: React.BaseSyntheticEvent) => {
      handleSubmit(onSubmit)(event).catch(console.error);
    },
    [handleSubmit, onSubmit]
  );

  return {
    submit,
    TitleInput: (
      <FormInput
        name={FieldsName.title}
        control={control}
        type={InputType.text}
        disabled={isLoading}
        label={intl.formatMessage({ id: 'app.sml.presave.form.title', defaultMessage: 'Track title' })}
        placeholder={intl.formatMessage({
          id: 'app.sml.presave.form.title_placeholder',
          defaultMessage: 'The name of the release',
        })}
      />
    ),
    ArtistNameInput: (
      <FormInput
        name={FieldsName.artistName}
        control={control}
        type={InputType.text}
        disabled={isLoading}
        label={intl.formatMessage({ id: 'app.sml.presave.form.artist', defaultMessage: 'Artist name' })}
        placeholder={intl.formatMessage({
          id: 'app.sml.presave.form.artist_placeholder',
          defaultMessage: 'The name fans will see',
        })}
      />
    ),
    ReleaseDateInput: (
      <PresaveDateField
        name={FieldsName.releaseDate}
        control={control}
        disabled={isLoading}
        min={today}
        label={intl.formatMessage({ id: 'app.sml.presave.form.release_date', defaultMessage: 'Release date' })}
        help={intl.formatMessage({
          id: 'app.sml.presave.form.release_date_help',
          defaultMessage: 'The day you plan to put the track out. You mark the release yourself when it lands.',
        })}
      />
    ),
  };
}
